import { createConsumer } from '@rails/actioncable';

const PRESENCE_INTERVAL = 30000;
const RECONNECT_INTERVAL = 1000;

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(
    app,
    pubsubToken = null,
    websocketHost = '',
    channelName = 'RoomChannel'
  ) {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.consumer = createConsumer(websocketURL);
    this.app = app;
    this.channelName = channelName;
    this.pubsubToken = pubsubToken;
    this.subscription = pubsubToken
      ? this.createSubscription(pubsubToken)
      : null;
    this.events = {};
    this.reconnectTimer = null;
    this.isAValidEvent = () => true;
    this.triggerPresenceInterval = () => {
      setTimeout(() => {
        this.subscription?.updatePresence();
        this.triggerPresenceInterval();
      }, PRESENCE_INTERVAL);
    };
    this.triggerPresenceInterval();
  }

  createSubscription(pubsubToken) {
    return this.consumer.subscriptions.create(
      {
        channel: this.channelName,
        pubsub_token: pubsubToken,
        account_id: this.app.$store.getters.getCurrentAccountId,
        user_id: this.app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        connected: () => {
          if (this.pendingLazySessionSync) {
            this.pendingLazySessionSync = false;
            this.onLazySessionSubscribed?.();
          }
        },
        disconnected: () => {
          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
          this.initReconnectTimer();
        },
      }
    );
  }

  // Move this connector to another stream on the SAME socket, without reloading the page.
  //
  // A pubsub token is a session identity: when the server deletes the contact_inbox behind it, the
  // stream stays technically subscribable but nothing is ever published to it again. Rebuilding the
  // page would repair that, but at the cost of minting a brand new contact_inbox — which is the
  // thing being merged away in the first place, so recovery-by-reload feeds the loop it recovers
  // from. Re-subscribing costs one unsubscribe + one subscribe and creates nothing.
  //
  // Returns false when there is nothing to do, so callers can skip their follow-up work.
  resubscribe(pubsubToken) {
    if (!pubsubToken || pubsubToken === this.pubsubToken) return false;

    // Drop the old subscription first: the consumer keeps a list, and leaking the dead one leaves a
    // second `received` handler wired to this same connector.
    this.subscription?.unsubscribe();
    this.pubsubToken = pubsubToken;
    this.subscription = this.createSubscription(pubsubToken);
    return true;
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      this.onReconnect();
      BaseActionCableConnector.isDisconnected = false;
    } else {
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => { };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => { };

  disconnect() {
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
